import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCartWorkflowStore } from '@/state/cartWorkflowStore';
import { useCustomerSessionStore } from '@/state/customerSessionStore';

const MAX_TIMER_DELAY = 2_147_000_000;

function expiresIn(value?: string | null) {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp - Date.now() : 0;
}

/**
 * Storage parsing protects reloads; this guard protects a tab that remains open
 * while its ordering/cart capabilities expire.
 */
export function CartExpiryGuard() {
  const sessionExpiresAt = useCustomerSessionStore((state) => state.expiresAt);
  const clearSession = useCustomerSessionStore((state) => state.clearSession);
  const recoveryExpiresAt = useCartWorkflowStore((state) => state.recovery?.expiresAt);
  const pendingExpiresAt = useCartWorkflowStore((state) => state.pendingRecovery?.expiresAt);
  const clearRecovery = useCartWorkflowStore((state) => state.clearRecovery);
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkExpiry = () => {
      const sessionExpired = sessionExpiresAt !== null && (expiresIn(sessionExpiresAt) ?? 0) <= 0;
      const cartExpiry = recoveryExpiresAt ?? pendingExpiresAt;
      const cartExpired = cartExpiry !== undefined && (expiresIn(cartExpiry) ?? 0) <= 0;

      if (sessionExpired) {
        clearSession();
        clearRecovery();
        queryClient.removeQueries({ queryKey: ['public-cart'] });
        queryClient.removeQueries({ queryKey: ['public-cart-promotions'] });
        return;
      }
      if (cartExpired) {
        clearRecovery();
        queryClient.removeQueries({ queryKey: ['public-cart'] });
        queryClient.removeQueries({ queryKey: ['public-cart-promotions'] });
      }
    };

    checkExpiry();
    const delays = [sessionExpiresAt, recoveryExpiresAt, pendingExpiresAt]
      .map(expiresIn)
      .filter((value): value is number => value !== null && value > 0);
    const delay = delays.length
      ? Math.min(Math.min(...delays) + 25, MAX_TIMER_DELAY)
      : MAX_TIMER_DELAY;
    const timer = window.setTimeout(checkExpiry, delay);
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkExpiry();
    };
    window.addEventListener('focus', checkExpiry);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', checkExpiry);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [
    clearRecovery,
    clearSession,
    pendingExpiresAt,
    queryClient,
    recoveryExpiresAt,
    sessionExpiresAt,
  ]);

  return null;
}
